/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react';
import { useHookstate, type StateMethods } from '@hookstate/core';
import { useLang } from '~/hooks/useLang';
import { getWarehouseCheckoutById } from '../../services';
import { Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close'
import type { IWarehouseCheckout } from '../../interface';
import dayjs from 'dayjs';
import TableEquipments from '../TableEquipments';
import TableMaterials from '../TableMaterials';

export interface ViewMoreProps {
  readonly open: StateMethods<boolean>
  readonly itemId: number | null
  readonly handleClose: () => void
}

export default function ViewMore({ open, itemId, handleClose }: ViewMoreProps) {
  const { translate } = useLang()
  const warehouseCheckout = useHookstate<IWarehouseCheckout | null>(null)
  const loading = useHookstate<boolean>(false)

  useEffect(() => {
    if (itemId) {
      loading.set(true)
      getWarehouseCheckoutById(itemId)
        .then((response) => {
          warehouseCheckout.set(response)
        }).finally(() => loading.set(false))
    }
  }, [itemId])

  const clickOutModalClose = (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick') {
      open.set(false)
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth={'lg'}
      open={open.get()}
      onClose={clickOutModalClose}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {translate('Cautela/Devolução')}
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading.get() ? (
          <Grid
            item
            xs={12}
            display={'flex'}
            sx={{ justifyContent: 'center', padding: 4 }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container>
            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {`${translate('Warehouse Name')}: `}
              </Typography>
              <Typography variant="subtitle2">
                {warehouseCheckout.get()?.warehouse.name ?? translate('Uninformed')}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {`${translate('Status')}: `}
              </Typography>
              <Typography variant="subtitle2">
                {warehouseCheckout.get()?.statusLabel ?? translate('Uninformed')}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {`${translate('Encarregado pela cautela')}: `}
              </Typography>
              <Typography variant="subtitle2">
                {`${warehouseCheckout.get()?.checkedOutByUser?.registration
                  ? `${warehouseCheckout.get()?.checkedOutByUser?.registration} - `
                  : ''}
                  ${warehouseCheckout.get()?.checkedOutByUser?.representationName
                    ? warehouseCheckout.get()?.checkedOutByUser?.representationName
                    : translate('Uninformed')}`}
              </Typography>
            </Grid>
            {warehouseCheckout.get()?.checkedInByUser &&
              <Grid item xs={3}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {`${translate('Encarregado pela devolução')}: `}
                </Typography>
                <Typography variant="subtitle2">
                  {`${warehouseCheckout.get()?.checkedInByUser?.registration
                    ? `${warehouseCheckout.get()?.checkedInByUser?.registration} - `
                    : ''}
                  ${warehouseCheckout.get()?.checkedInByUser?.representationName
                      ? warehouseCheckout.get()?.checkedInByUser?.representationName
                      : translate('Uninformed')}`}
                </Typography>
              </Grid>
            }
            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {`${translate('User')}: `}
              </Typography>
              <Typography variant="subtitle2">
                {`${warehouseCheckout.get()?.receivedByUser?.registration
                  ? `${warehouseCheckout.get()?.receivedByUser?.registration} - `
                  : ''}
                  ${warehouseCheckout.get()?.receivedByUser?.representationName
                    ? warehouseCheckout.get()?.receivedByUser?.representationName
                    : translate('Uninformed')}`}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="subtitle1" fontWeight={'bold'}>
                {`${translate('created_at')}: `}
              </Typography>
              <Typography variant="subtitle2">
                {warehouseCheckout.get()?.checkedInAt
                  ? dayjs(warehouseCheckout.get()?.checkedInAt).format('DD/MM/YYYY HH:mm:ss')
                  : translate('Uninformed')}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: 3 }}>
              <Typography variant="h6" fontWeight={'bold'} sx={{ marginBottom: 2 }}>
                {translate('Equipments')}
                <Chip
                  label={warehouseCheckout.get()?.checkoutEquipments.length}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <TableEquipments
                  equipments={warehouseCheckout.get()?.checkoutEquipments || []}
                  status={warehouseCheckout.get()?.status}
              />
            </Grid>
            <Grid item xs={12} sx={{ marginTop: 3 }}>
              <Typography variant="h6" fontWeight={'bold'} sx={{ marginBottom: 2 }}>
                {translate('Materials')}
                <Chip
                  label={warehouseCheckout.get()?.checkoutMaterials.length}
                  size="small"
                  color="secondary"
                  sx={{ marginLeft: 1 }}
                />
              </Typography>
              <TableMaterials
                materials={warehouseCheckout.get()?.checkoutMaterials || []}
                status={warehouseCheckout.get()?.status}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}